// Pure math for keeping the forecast clear of the rail layout's floating question.
// Every length is in timeline-LOCAL px (the root's CSS zoom divided out), the unit the spine
// is laid out in.

/** Clear space (local px) kept between the question's last line and the forecast's top. */
export const QUESTION_CLEARANCE_GAP = 8;

/** A new clearance is applied only when it moves by at least this much (local px). */
export const CLEARANCE_STEP = 2;

/** How far (local px) the user must scroll the timeline vertically before it counts as
 *  moving it themselves. A horizontal drag wobbles a few px vertically; that is not a move. */
export const USER_PAN_THRESHOLD = 6;

/** How long (ms) after a wheel / drag / arrow key a scroll still belongs to that gesture. */
export const GESTURE_WINDOW_MS = 1000;

/**
 * The effective zoom between client px and the element's own CSS px, from its rendered
 * width over its layout width. 1 when either is unknown.
 *
 * @param {number} rectWidth   getBoundingClientRect().width
 * @param {number} offsetWidth the element's offsetWidth
 */
export function clientZoom(rectWidth, offsetWidth) {
  return rectWidth > 0 && offsetWidth > 0 ? rectWidth / offsetWidth : 1;
}

/**
 * The topmost edge (client px) among rects that sit horizontally under `band`, or null when
 * none does. Empty rects are skipped.
 *
 * @param {{top: number, left: number, right: number, width: number, height: number}[]} rects
 * @param {{left: number, right: number}} band the question's horizontal extent
 */
export function topmostUnder(rects, band) {
  let top = null;
  for (const r of rects) {
    if (!(r.width > 0 && r.height > 0)) continue;
    if (r.right <= band.left || r.left >= band.right) continue;
    if (top === null || r.top < top) top = r.top;
  }
  return top;
}

/**
 * Where the forecast's top would sit (local px from the scroll box's top) with no clearance
 * applied and the timeline scrolled to the top. Measuring this way, rather than where it is
 * now, keeps the clearance from feeding back into its own measurement.
 *
 * @param {object} a
 * @param {number} a.clientTop     the content's top, client px
 * @param {number} a.originTop     the scroll box's top, client px
 * @param {number} a.zoom          client px per local px
 * @param {number} a.scrollTop     the scroll box's scrollTop (local px)
 * @param {number} a.appliedOffset the clearance in the DOM when it was measured
 */
export function contentTopAtRest({ clientTop, originTop, zoom, scrollTop, appliedOffset }) {
  return (clientTop - originTop) / zoom + scrollTop - appliedOffset;
}

/**
 * The downward shift (local px, never negative) that puts `contentTop` at least `gap` below
 * `questionBottom`. Both are measured from the same origin.
 */
export function neededClearance({ questionBottom, contentTop, gap = QUESTION_CLEARANCE_GAP }) {
  return Math.max(0, Math.ceil(questionBottom + gap - contentTop));
}

/**
 * The clearance to apply next: `needed` once it differs from `current` by at least `step`,
 * otherwise `current`, so sub-step measurement noise never moves the forecast.
 */
export function nextClearance(current, needed, step = CLEARANCE_STEP) {
  const want = Math.max(0, needed);
  return Math.abs(want - current) >= step ? want : current;
}

/**
 * Record a vertical-pan gesture (wheel, drag, arrow key). A gesture that starts after the
 * last one lapsed re-bases at the current scrollTop, so the threshold counts from where this
 * gesture began.
 *
 * @param {{at: number, baseline: number} | null} prev
 * @param {{now: number, scrollTop: number, windowMs?: number}} a
 * @returns {{at: number, baseline: number}}
 */
export function noteGesture(prev, { now, scrollTop, windowMs = GESTURE_WINDOW_MS }) {
  if (prev && now - prev.at <= windowMs) return { at: now, baseline: prev.baseline };
  return { at: now, baseline: scrollTop };
}

/**
 * Whether a scroll to `scrollTop` is the user moving the timeline vertically: it happened
 * during a gesture and has carried at least `threshold` px from where the gesture started.
 * A scroll with no gesture behind it (the browser clamping after the content shrank) is not.
 */
export function isUserVerticalMove(
  gesture,
  { now, scrollTop, windowMs = GESTURE_WINDOW_MS, threshold = USER_PAN_THRESHOLD },
) {
  if (!gesture || now - gesture.at > windowMs) return false;
  return Math.abs(scrollTop - gesture.baseline) >= threshold;
}
